import React, { useRef, useState } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Card, Popover, Spin, Tooltip } from 'antd';
import type { KpiSize } from '~containers/analytics/kpis/types';
import style from './style.module.scss';

export interface KpiCardShellProps {
    title: string;
    /** Muted line rendered beneath the title, describing this card's own data. */
    subtitle?: string;
    size: KpiSize;
    /** Optional content shown in the (i) info popover in the card header. */
    info?: React.ReactNode;
    /** How the (i) info popover opens. Defaults to 'click'; the RPP dashboard uses 'hover'. */
    infoTrigger?: 'hover' | 'click';
    /** Colours the (i) amber: something limits the card's figure. */
    infoTone?: 'warning';
    /** Set when `info` holds a control: the (i) then takes focus and opens on
     * Enter, and the bubble follows it in tab order. */
    infoInteractive?: boolean;
    isLoading?: boolean;
    /** Shown under the spinner, for a load worth explaining. */
    loadingNote?: string;
    /**
     * Optional persistent control shown at the left of the header-right cluster
     * (e.g. the "Coupon activated" card's coupon dropdown). Unlike `headerAction`
     * (an edit affordance), this stays visible in view mode. Omitted for every
     * card without a selector, which then renders exactly as before.
     */
    headerControl?: React.ReactNode;
    /** Optional action in the top-right corner of the title row (e.g. a per-card edit menu). */
    headerAction?: React.ReactNode;
    /** When true, renders a subtle dot in the header indicating the card has an active override (view mode only). */
    modified?: boolean;
    /** When true, the card holds placeholder sample data: shows a "Sample" tag and grays out the content. */
    sample?: boolean;
    /** When true, renders a highlighted ring around the card (e.g. a partial/degraded data state). */
    highlight?: boolean;
    children: React.ReactNode;
}

/**
 * Generic chrome for a KPI card: antd `Card`, title, optional (i) info popover,
 * loading state. It is the grid item, so it owns the `grid-column` span derived
 * from `size`.
 */
const SIZE_SPAN_CLASS: Record<KpiSize, string> = {
    sm: style.span_sm,
    md: style.span_md,
    lg: style.span_lg,
};

const infoIconClass = (tone: 'warning' | undefined) =>
    `${style.kpi_card_info_icon} ${
        tone === 'warning' ? style.kpi_card_info_icon_warning : ''
    }`;

const infoIconLabel = (tone: 'warning' | undefined) =>
    tone === 'warning' ? 'Info, with a warning' : 'Info';

/**
 * The (i) bubble when it holds a control. The bubble renders inside the anchor,
 * right after the (i), so Tab goes from the (i) to the control and on out of the
 * card; focus leaving the anchor closes it.
 */
const InteractiveInfo: React.FC<{
    info: React.ReactNode;
    trigger: 'hover' | 'click';
    tone: 'warning' | undefined;
}> = ({ info, trigger, tone }) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLSpanElement>(null);
    const iconRef = useRef<HTMLSpanElement>(null);

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape' && open) {
            setOpen(false);
            iconRef.current?.focus();
        } else if (
            (event.key === 'Enter' || event.key === ' ') &&
            event.target === iconRef.current
        ) {
            event.preventDefault();
            setOpen(!open);
        }
    };

    const onBlur = (event: React.FocusEvent) => {
        const next = event.relatedTarget;
        if (next instanceof Node && !anchorRef.current?.contains(next)) {
            setOpen(false);
        }
    };

    return (
        <span
            ref={anchorRef}
            className={style.kpi_card_info_anchor}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
        >
            <Popover
                content={info}
                trigger={trigger}
                placement="bottomRight"
                open={open}
                onOpenChange={setOpen}
                getPopupContainer={() => anchorRef.current ?? document.body}
            >
                <InfoCircleOutlined
                    ref={iconRef}
                    className={infoIconClass(tone)}
                    aria-label={infoIconLabel(tone)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={open}
                />
            </Popover>
        </span>
    );
};

export const KpiCardShell: React.FC<KpiCardShellProps> = ({
    title,
    subtitle,
    size,
    info,
    infoTrigger = 'click',
    infoTone,
    infoInteractive = false,
    isLoading = false,
    loadingNote,
    headerControl,
    headerAction,
    modified = false,
    sample = false,
    highlight = false,
    children,
}) => (
    <Card
        className={`${style.kpi_card_shell} ${SIZE_SPAN_CLASS[size]} ${
            sample ? style.kpi_card_sample : ''
        } ${highlight ? style.kpi_card_highlight : ''}`}
    >
        <div className={style.kpi_card_header}>
            <div className={style.kpi_card_title_group}>
                <span
                    className={`${style.kpi_card_title} footnote_description`}
                >
                    {title}
                </span>
                {subtitle && (
                    <span className={style.kpi_card_subtitle}>{subtitle}</span>
                )}
            </div>
            <div className={style.kpi_card_header_right}>
                {headerControl && (
                    <div className={style.kpi_card_header_control}>
                        {headerControl}
                    </div>
                )}
                {sample && (
                    <Tooltip title="Sample data, not connected to a real source">
                        <span className={style.kpi_card_sample_tag}>
                            Sample
                        </span>
                    </Tooltip>
                )}
                {modified && (
                    <Tooltip title="Modified">
                        <span
                            className={style.kpi_card_modified_dot}
                            aria-label="Modified"
                        />
                    </Tooltip>
                )}
                {info && !infoInteractive && (
                    <Popover
                        content={info}
                        trigger={infoTrigger}
                        placement="bottomRight"
                    >
                        <InfoCircleOutlined
                            className={infoIconClass(infoTone)}
                            aria-label={infoIconLabel(infoTone)}
                        />
                    </Popover>
                )}
                {info && infoInteractive && (
                    <InteractiveInfo
                        info={info}
                        trigger={infoTrigger}
                        tone={infoTone}
                    />
                )}
                {headerAction && (
                    <div className={style.kpi_card_header_action}>
                        {headerAction}
                    </div>
                )}
            </div>
        </div>

        {isLoading ? (
            <div className={style.kpi_card_spinner}>
                <Spin />
                {loadingNote && (
                    <span className="footnote_description">{loadingNote}</span>
                )}
            </div>
        ) : (
            <div className={style.kpi_card_content}>{children}</div>
        )}
    </Card>
);
